import styled from '@emotion/styled';
import {media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {Card} from 'UI/components/layout/Card';
import {H4, P} from 'UI/components/layout/Text';

import {FadeInAnimation} from 'UI/animations/shared';

import {type JobTypeMap, useJobsCard} from './useJobsCard';
import {Book} from 'Images/icons';

/**
 * The `ComponentProps` interface defines the properties for the `JobsCard` component.
 * This interface specifies all the required and optional properties needed to render a job posting card with proper structure and content.
 * It ensures type safety for job data including employment type, content details, and accessibility identifiers for comprehensive job display functionality.
 */
interface ComponentProps {
    /**
     * The type of employment for the job posting, which must match one of the predefined job type mapping keys.
     * This property determines how the employment type will be displayed and formatted in the job card interface.
     * The value should correspond to valid employment categories like full-time, part-time, contract, or internship positions.
     */
    employmentType: keyof typeof JobTypeMap;
    /**
     * The main title or headline of the job posting that will be prominently displayed in the card header.
     * This property serves as the primary identifier and attraction point for potential job applicants browsing through listings.
     * The headline should be concise yet descriptive enough to convey the essential job role and responsibilities.
     */
    headline: string;
    /**
     * The unique identifier for the job posting used for internal tracking and accessibility purposes.
     * This property ensures each job card can be uniquely referenced and helps maintain proper ARIA labeling for screen readers.
     * The ID is also used to create unique element identifiers within the component's DOM structure.
     */
    id: string;
    /**
     * The description text that provides a brief overview of the job posting content displayed in the card body.
     * This property contains the summarized job details that help candidates understand the role before clicking through to the full posting.
     * The description is automatically truncated to maintain consistent card layouts across different job listings.
     */
    listDescription: string;
    /**
     * The URL slug or path that links to the detailed job posting page for complete job information.
     * This property enables navigation to the full job description where candidates can learn more details and submit applications.
     * The slug should be properly formatted as a valid URL path that corresponds to the job's detail page route.
     */
    slug: string;
    /**
     * The test identifier for component testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'JobsCard'
     */
    testId?: string;
}

/**
 * The JobsCard component renders a job posting card with employment type, headline, description, and application button.
 * This component displays job information in a structured card layout with proper accessibility attributes and semantic HTML.
 * It uses the useJobsCard hook to format the employment type and provides a complete job preview with call-to-action functionality.
 *
 * @param props                 The component props.
 * @param props.employmentType  The type of employment for the job posting.
 * @param props.headline        The main title/headline of the job posting.
 * @param props.id              The unique identifier for the job posting.
 * @param props.listDescription The description text displayed in the job card.
 * @param props.slug            The URL slug for the job posting detail page.
 * @param props.testId          The test identifier for component testing purposes.
 * @returns The JobsCard component JSX structure with job information and application button.
 *
 * @example
 * ```tsx
 * <JobsCard
 *     employmentType="fullTime"
 *     headline="Senior Software Engineer"
 *     id="job-123"
 *     listDescription="We are looking for an experienced software engineer..."
 *     slug="/jobs/senior-software-engineer"
 *     testId="jobs-card-123"
 * />
 * ```
 */
const JobsCard = observer(({
    employmentType,
    headline,
    id,
    listDescription,
    slug,
    testId = 'JobsCard'
}: ComponentProps) => {
    const {type} = useJobsCard(employmentType);

    return (
        <Card data-cy={testId} span={8}>
            <JobType variants={FadeInAnimation}><Book color1="currentColor" />{type}</JobType>
            <Spacer y={4} />
            <MotionH4 as="h3" id={`card-headline-${id}`} variants={FadeInAnimation}>{headline}</MotionH4>
            <Spacer y={4} />
            <StyledDesc id={`card-description-${id}`} variants={FadeInAnimation}>{listDescription}</StyledDesc>
            <Spacer y={20} />
            <motion.div variants={FadeInAnimation}>
                <Button
                    aria-describedby={`card-description-${id}`}
                    aria-labelledby={`card-headline-${id}`}
                    as="link"
                    href={slug}
                >
                    Jetzt bewerben
                </Button>
            </motion.div>
        </Card>
    );
});

JobsCard.displayName = 'JobsCard';

export {JobsCard};

const JobType = styled(motion.create(P))`
    align-items: center;
    color: ${({theme}) => theme.colors.textSecondary};
    display: flex;
    font-size: 1.6rem;
    gap: ${spacing(3)};
    line-height: 1;

    ${media('md')} {
        font-size: 1.8rem;
    }

    ${media('lg')} {
        font-size: 2rem;
    }
`;

const MotionH4 = motion.create(H4);

const StyledDesc = styled(motion.create(P))`
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    display: -webkit-box;
    overflow: hidden;
    white-space: pre-wrap;
`;